import { useUser } from '@/lib/auth0'
import CONFIG from '@/lib/config'
import { LDProvider } from 'launchdarkly-react-client-sdk'
import { ReactNode, useMemo } from 'react'
import Loading from '@/components/Loading'

interface LaunchDarklyProviderProps {
  children: ReactNode
}

export function LaunchDarklyProvider({ children }: LaunchDarklyProviderProps) {
  const { data: userData, auth0 } = useUser()

  const context = useMemo(() => {
    // Return anonymous context ff auth0 is still loading
    // or if user is not authenticated
    if (auth0.isLoading || !userData) {
      return {
        kind: 'user',
        key: 'loading',
        anonymous: true,
      }
    }

    const { dataAccess, user } = userData

    const allBrandIds =
      dataAccess?.brands
        .flatMap(brand => brand.brand_id)
        .filter(brandId => brandId) || []
    const firstBrandId = allBrandIds.length > 0 ? allBrandIds[0] : 'unknown'

    return {
      kind: 'multi',
      'corporation-id': {
        key: dataAccess?.corporation_id || 'unknown',
      },
      'brand-id': {
        key: firstBrandId,
        brand_ids: allBrandIds,
      },
      'user-id': {
        key: user.email || 'unknown',
        name:
          `${user.given_name || ''} ${user.family_name || ''}`.trim() ||
          'unknown',
      },
    }
  }, [userData, auth0.isLoading])

  if (auth0.isLoading) {
    return <Loading />
  }

  return (
    <LDProvider
      clientSideID={CONFIG.LAUNCH_DARKLY_KEY}
      context={context}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
    >
      {children}
    </LDProvider>
  )
}
